// ============================================================
// Chess Arena — Main Menu Screen
// Royal Match / Supercell style main menu with full meta UI
// ============================================================

import type { Screen } from '../screenManager';
import type { PlayerStateData, LevelConfig } from '../../core/types';
import { renderChestSlots, updateChestTimers } from '../chestUI';

export class MainMenuScreen implements Screen {
  readonly name = 'mainMenu';
  private container!: HTMLElement;
  private playerState: PlayerStateData | null = null;
  private nextLevel: LevelConfig | null = null;

  // Callbacks
  private onPlayLevel: ((level: number) => void) | null = null;
  private onOpenTasks: (() => void) | null = null;
  private onChestTapLocked: ((index: number) => void) | null = null;
  private onChestTapUnlocking: ((index: number) => void) | null = null;
  private onChestTapReady: ((index: number) => void) | null = null;
  private getRushCost: ((index: number) => number) | null = null;
  private onOpenCollection: (() => void) | null = null;

  // DOM references
  private coinsEl!: HTMLElement;
  private livesEl!: HTMLElement;
  private trophiesEl!: HTMLElement;
  private xpLevelEl!: HTMLElement;
  private opponentPortraitEl!: HTMLElement;
  private opponentNameEl!: HTMLElement;
  private opponentRatingEl!: HTMLElement;
  private chestSlotsContainer!: HTMLElement;

  private levelBtn!: HTMLButtonElement;
  private levelLabelEl!: HTMLElement;
  private tasksBadgeEl!: HTMLElement;

  // No-lives popup
  private noLivesPopup!: HTMLElement;
  private noLivesTimerEl!: HTMLElement;
  private noLivesInterval: ReturnType<typeof setInterval> | null = null;

  setPlayerState(state: PlayerStateData): void {
    this.playerState = state;
  }

  setNextLevel(config: LevelConfig): void {
    this.nextLevel = config;
  }

  setOnPlayLevel(cb: (level: number) => void): void {
    this.onPlayLevel = cb;
  }

  setOnOpenTasks(cb: () => void): void {
    this.onOpenTasks = cb;
  }

  setOnChestTapLocked(cb: (index: number) => void): void {
    this.onChestTapLocked = cb;
  }

  setOnChestTapUnlocking(cb: (index: number) => void): void {
    this.onChestTapUnlocking = cb;
  }

  setOnChestTapReady(cb: (index: number) => void): void {
    this.onChestTapReady = cb;
  }

  setGetRushCost(cb: (index: number) => number): void {
    this.getRushCost = cb;
  }

  setOnOpenCollection(cb: () => void): void {
    this.onOpenCollection = cb;
  }

  init(container: HTMLElement): void {
    this.container = container;
    container.classList.add('main-menu-screen');

    container.innerHTML = `
      <!-- Top Bar -->
      <div class="mm-top-bar">
        <div class="mm-stat-pill">
          <span class="mm-stat-icon"><img src="/assets/ui/icon_coin.png" alt="" width="44" height="44"><span class="mm-stat-plus">+</span></span>
          <span data-ref="coins">0</span>
        </div>
        <div class="mm-stat-pill">
          <span class="mm-stat-icon"><img src="/assets/ui/icon_heart.png" alt="" width="44" height="44"></span>
          <span data-ref="lives">5</span>
        </div>
        <div class="mm-stat-pill">
          <span class="mm-stat-icon"><img src="/assets/ui/icon_trophy.png" alt="" width="44" height="44"></span>
          <span data-ref="trophies">0</span>
        </div>
        <div class="mm-stat-pill">
          <span class="mm-stat-icon"><img src="/assets/ui/icon_xp_star.png" alt="" width="44" height="44" class="mm-xp-star-icon"></span>
          <span data-ref="xpLevel">0</span>
        </div>
      </div>

      <!-- Center Area -->
      <div class="mm-center">
        <div class="mm-opponent-preview" data-ref="opponentPreview" style="display: none;">
          <div class="mm-opponent-portrait" data-ref="opponentPortrait"></div>
          <div class="mm-opponent-info">
            <div class="mm-opponent-name" data-ref="opponentName"></div>
            <div class="mm-opponent-rating" data-ref="opponentRating"></div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="mm-actions">
        <button class="ca-btn ca-btn-green" data-ref="levelBtn">
          <span data-ref="levelLabel">Level 1</span>
        </button>
        <button class="ca-btn ca-btn-orange" data-ref="tasksBtn" style="position: relative;">
          Tasks
          <span class="mm-tasks-badge" data-ref="tasksBadge" style="display: none;">0</span>
        </button>
      </div>

      <!-- Chest Tray -->
      <div class="mm-chest-tray">
        <div class="mm-chest-slots" data-ref="chestSlots"></div>
      </div>

      <!-- Bottom Nav -->
      <div class="mm-bottom-nav">
        <div class="mm-nav-tab">
          <span class="mm-nav-icon"><img src="/assets/ui/icon_nav_leaderboard.png" alt=""></span>
        </div>
        <div class="mm-nav-tab" data-ref="collectionTab">
          <span class="mm-nav-icon"><img src="/assets/ui/icon_nav_collection.png" alt=""></span>
        </div>
        <div class="mm-nav-tab mm-nav-tab-active">
          <span class="mm-nav-icon"><img src="/assets/ui/icon_home.png" alt=""></span>
          <span>Home</span>
        </div>
        <div class="mm-nav-tab">
          <span class="mm-nav-icon"><img src="/assets/ui/icon_nav_social.png" alt=""></span>
        </div>
        <div class="mm-nav-tab">
          <span class="mm-nav-icon"><img src="/assets/ui/icon_nav_store.png" alt=""></span>
        </div>
      </div>
    `;

    // Cache DOM references
    this.coinsEl = container.querySelector('[data-ref="coins"]')!;
    this.livesEl = container.querySelector('[data-ref="lives"]')!;
    this.trophiesEl = container.querySelector('[data-ref="trophies"]')!;
    this.xpLevelEl = container.querySelector('[data-ref="xpLevel"]')!;
    this.opponentPortraitEl = container.querySelector('[data-ref="opponentPortrait"]')!;
    this.opponentNameEl = container.querySelector('[data-ref="opponentName"]')!;
    this.opponentRatingEl = container.querySelector('[data-ref="opponentRating"]')!;
    this.chestSlotsContainer = container.querySelector('[data-ref="chestSlots"]')!;

    this.levelBtn = container.querySelector('[data-ref="levelBtn"]')! as HTMLButtonElement;
    this.levelLabelEl = container.querySelector('[data-ref="levelLabel"]')!;
    this.tasksBadgeEl = container.querySelector('[data-ref="tasksBadge"]')!;

    // No-lives popup (hidden by default)
    this.noLivesPopup = document.createElement('div');
    this.noLivesPopup.className = 'no-lives-popup hidden';
    this.noLivesPopup.innerHTML = `
      <div class="no-lives-backdrop"></div>
      <div class="no-lives-card">
        <div class="no-lives-title">You're out of lives!</div>
        <div class="no-lives-icon"><img src="/assets/ui/icon_heart.png" alt="" width="48" height="48"></div>
        <div class="no-lives-timer" data-ref="noLivesTimer"></div>
        <button class="ca-btn ca-btn-orange no-lives-buy-btn">Buy more</button>
        <button class="no-lives-dismiss">Dismiss</button>
      </div>
    `;
    container.appendChild(this.noLivesPopup);
    this.noLivesTimerEl = this.noLivesPopup.querySelector('[data-ref="noLivesTimer"]')!;

    // Close popup on backdrop or dismiss tap
    this.noLivesPopup.querySelector('.no-lives-backdrop')!.addEventListener('click', () => this.hideNoLivesPopup());
    this.noLivesPopup.querySelector('.no-lives-dismiss')!.addEventListener('click', () => this.hideNoLivesPopup());

    // Event listeners
    this.levelBtn.addEventListener('click', () => {
      if (this.playerState && this.playerState.lives > 0) {
        this.onPlayLevel?.(this.playerState.currentLevel);
      } else if (this.playerState) {
        this.showNoLivesPopup();
      }
    });

    const tasksBtn = container.querySelector('[data-ref="tasksBtn"]')!;
    tasksBtn.addEventListener('click', () => {
      this.onOpenTasks?.();
    });

    const collectionTab = container.querySelector('[data-ref="collectionTab"]')!;
    collectionTab.addEventListener('click', () => {
      this.onOpenCollection?.();
    });
  }

  show(data?: Record<string, unknown>): void {
    if (data?.playerState) {
      this.playerState = data.playerState as PlayerStateData;
    }
    if (data?.nextLevel) {
      this.nextLevel = data.nextLevel as LevelConfig;
    }
    this.refresh();
  }

  hide(): void {
    // No cleanup needed
  }

  update(): void {
    // Update chest timers every second
    if (this.chestSlotsContainer) {
      const anyReady = updateChestTimers(this.chestSlotsContainer);
      if (anyReady) {
        this.refresh(); // Re-render to show "ready" state
      }
    }
    // Update lives regen timer
    this.refreshLives();
  }

  destroy(): void {
    this.container.innerHTML = '';
  }

  /** Refresh all UI elements from current state. */
  private refresh(): void {
    const state = this.playerState;
    if (!state) return;

    // Top bar stats
    this.coinsEl.textContent = this.formatNumber(state.coins);
    this.trophiesEl.textContent = this.formatNumber(state.trophies);
    this.xpLevelEl.textContent = String(Math.max(0, state.currentLevel - 1));
    this.refreshLives();

    // Opponent preview — only show when data is available
    const previewEl = this.container.querySelector('[data-ref="opponentPreview"]') as HTMLElement;
    if (this.nextLevel) {
      this.opponentPortraitEl.textContent = this.nextLevel.opponent.portrait;
      this.opponentNameEl.textContent = this.nextLevel.opponent.name;
      this.opponentRatingEl.textContent = `\uD83C\uDFC6 ${this.nextLevel.opponent.rating}`;
      if (previewEl) previewEl.style.display = '';
    } else {
      if (previewEl) previewEl.style.display = 'none';
    }

    // Level button
    this.levelLabelEl.textContent = `Level ${state.currentLevel}`;
    // Don't disable — click handler shows no-lives popup when out of lives

    // Tasks badge
    const unclaimedCount = state.dailyTasks.filter(
      (t) => t.progress >= t.config.target && !t.claimed
    ).length;
    if (unclaimedCount > 0) {
      this.tasksBadgeEl.textContent = String(unclaimedCount);
      this.tasksBadgeEl.style.display = '';
    } else {
      this.tasksBadgeEl.style.display = 'none';
    }

    // Chest tray
    renderChestSlots(this.chestSlotsContainer, state.chestSlots, {
      onTapLocked: (i) => this.onChestTapLocked?.(i),
      onTapUnlocking: (i) => this.onChestTapUnlocking?.(i),
      onTapReady: (i) => this.onChestTapReady?.(i),
      getRushCost: (i) => this.getRushCost?.(i) ?? 0,
    });

  }

  private showNoLivesPopup(): void {
    this.noLivesPopup.classList.remove('hidden');
    this.updateNoLivesTimer();
    this.noLivesInterval = setInterval(() => this.updateNoLivesTimer(), 1000);
  }

  private hideNoLivesPopup(): void {
    this.noLivesPopup.classList.add('hidden');
    if (this.noLivesInterval) {
      clearInterval(this.noLivesInterval);
      this.noLivesInterval = null;
    }
  }

  private updateNoLivesTimer(): void {
    const state = this.playerState;
    if (!state) return;
    const regenInterval = 20 * 60 * 1000;
    const elapsed = Date.now() - state.lastLifeRegenTimestamp;
    const remaining = Math.max(0, regenInterval - elapsed);
    const mins = Math.floor(remaining / 60000);
    const secs = Math.floor((remaining % 60000) / 1000);
    this.noLivesTimerEl.textContent = `Next life in ${mins}:${String(secs).padStart(2, '0')}`;
    // Auto-close if lives regenerated
    if (remaining <= 0) {
      this.hideNoLivesPopup();
    }
  }

  private refreshLives(): void {
    const state = this.playerState;
    if (!state) return;

    if (state.lives >= state.maxLives) {
      this.livesEl.textContent = 'Full';
    } else {
      // Show count + regen hint
      const regenInterval = 20 * 60 * 1000; // 20 minutes
      const elapsed = Date.now() - state.lastLifeRegenTimestamp;
      const remaining = Math.max(0, regenInterval - elapsed);
      if (remaining > 0) {
        const mins = Math.ceil(remaining / 60000);
        this.livesEl.textContent = `${state.lives} (${mins}m)`;
      } else {
        this.livesEl.textContent = String(state.lives);
      }
    }
  }

  private formatNumber(n: number): string {
    if (n >= 10000) return `${(n / 1000).toFixed(1)}k`;
    return n.toLocaleString();
  }

}
